import React, { memo } from 'react';
import { useTranslation } from 'react-i18next';

import WithWizard from 'modules/views/shared/Wizard/WithWizard';
import NavigationButtons from 'modules/views/shared/NavigationButtons';
import Checkbox from 'modules/views/shared/Checkbox';
import Loader from 'modules/views/shared/Loader';
import Image from '../Image';

import { useStyles } from './styles';

export interface IConsentDocumentStepProps {
  htmlContent?: string;
  checkboxLabel: React.ReactNode;
  isChecked: boolean;
  onCheckChange: (checked: boolean) => void;
  onBack?: () => void;
  onNext: () => void;
  isFirstStep?: boolean;
  isLoading?: boolean;
  signatureUrl?: string;
  isSignatureApplied?: boolean;
  onSignatureAppliedChange?: (checked: boolean) => void;
  nextButtonLabel?: string;
  withoutWizardWrapper?: boolean;
  fields?: {
    name: string;
    id: string;
    isRequired: boolean;
  }[];
}

/**
 * Shared presentational step for the registration consent document sub-steps
 * (Terms & Conditions, Standard Project Consent Form, Standard Biometric
 * Consent). Renders the agreement HTML (title/subtitle are part of the HTML),
 * an always-enabled acknowledgement checkbox, and Back/Next navigation.
 */
const ConsentDocumentStep = ({
  htmlContent,
  checkboxLabel,
  isChecked,
  onCheckChange,
  onBack,
  onNext,
  isFirstStep = false,
  isLoading = false,
  signatureUrl,
  isSignatureApplied = false,
  onSignatureAppliedChange,
  nextButtonLabel,
  withoutWizardWrapper = false,
  fields = [],
}: IConsentDocumentStepProps) => {
  const classes = useStyles();
  const { t } = useTranslation();

  // When embedded inside an existing Wizard (withoutWizardWrapper), surface isLoading
  // on the button so the surrounding layout stays intact. When standalone, replace
  // the full content area with a loader (disables sidebar navigation too).
  const showFullscreenLoader = isLoading && !withoutWizardWrapper;

  const content = showFullscreenLoader ? (
    <div className={classes.loaderContainer}>
      <Loader styleClass={classes.loader} />
    </div>
  ) : (
    <div>
      <div className={classes.scrollContainer}>
        <div className={classes.documentContent} dangerouslySetInnerHTML={{ __html: htmlContent as string }} />

        <Checkbox
          label={checkboxLabel}
          checked={isChecked}
          styleClass={classes.acceptCheckbox}
          onChange={e => onCheckChange(e.target.checked)}
        />
      </div>

      {signatureUrl && (
        <div className={classes.applySignatureSection}>
          <div className={classes.applySignatureCard}>
            <div className={classes.applySignatureCardHeader}>
              <Checkbox
                label={
                  <div className={classes.applySignatureTextBlock}>
                    <p className={classes.applySignatureTitle}>{t('registrationConsent.applySignatureTitle')}</p>
                    <p className={classes.applySignatureSubtext}>{t('registrationConsent.applySignatureSubtext')}</p>
                  </div>
                }
                checked={isSignatureApplied}
                styleClass={classes.signatureCardCheckbox}
                onChange={e => onSignatureAppliedChange?.(e.target.checked)}
              />
            </div>
          </div>
          {isSignatureApplied && (
            <div className={classes.signatureContainer}>
              <Image
                src={signatureUrl}
                alt={t('myProfile.signature.sectionTitle')}
                className={classes.signatureImage}
              />
            </div>
          )}
        </div>
      )}

      <NavigationButtons
        secondaryButtonProps={{
          label: t('common.back'),
          isHidden: isFirstStep,
          onClick: onBack,
        }}
        primaryButtonProps={{
          label: nextButtonLabel || t('common.next'),
          hasEndArrow: true,
          disabled: !isChecked || (!!signatureUrl && !isSignatureApplied),
          isLoading: withoutWizardWrapper ? isLoading : undefined,
          onClick: onNext,
        }}
      />
    </div>
  );

  if (withoutWizardWrapper) {
    return <div>{content}</div>;
  }

  return <WithWizard disableNavigation={showFullscreenLoader} content={content} fields={fields} />;
};

export default memo(ConsentDocumentStep);
